import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug } from '../lib/cms';
import ContactForm from '../components/ContactForm';

export default function Contact() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchPageBySlug('kontakt');
      setPage(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="pb-24 space-y-16 lg:space-y-20 bg-slate-50/40">
      <Helmet>
        <title>{page?.meta_title || 'Kontakt os - CRE8IT'}</title>
        <meta name="description" content={page?.meta_description || 'Kontakt CRE8IT for et uforpligtende tilbud på skilte, interiørfolie, bildekoration eller solfilm.'} />
      </Helmet>

      {/* 1. Header & Primære Kontaktoplysninger ØVERST på siden */}
      <section className="relative overflow-hidden py-12 lg:py-16 border-b border-[#245b53]/30 bg-[#325d52]">
        {/* Subtile dekorative lysglød */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-96 w-96 rounded-full bg-[#deecd4]/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-8">
          
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#deecd4] px-3.5 py-1 text-xs font-bold text-[#325d52] border border-[#deecd4] shadow-xs">
              <i className="fa-solid fa-headset" />
              <span>Personlig Betjening & Hurtigt Svar</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl leading-tight">
              {page?.headline ? (
                page.headline
              ) : (
                <>
                  Kontakt os – vi står klar til at{' '}
                  <span className="text-[#deecd4]">hjælpe dig</span>
                </>
              )}
            </h1>
            <div className="flex items-center gap-1.5 pt-0.5">
              <div className="h-1.5 w-12 rounded-full bg-[#deecd4]" />
              <div className="h-1.5 w-3 rounded-full bg-white/40" />
              <div className="h-1.5 w-1.5 rounded-full bg-[#deecd4]" />
            </div>
            <p className="text-base sm:text-lg text-emerald-50/90 leading-relaxed">
              Ring til os, send en mail eller benyt kontaktformularen nedenfor for at få et uforpligtende tilbud på din opgave.
            </p>
          </div>

          {/* Primære kontaktoplysninger kort (Øverst før formularen) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            
            {/* Telefon */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-red via-[#34d399] to-brand-green-light opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green-light/30 text-brand-red mb-4">
                <i className="fa-solid fa-phone text-lg" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Telefon</h3>
              <div className="mt-2 space-y-1">
                <div>
                  <a
                    href="tel:70604788"
                    className="text-lg font-bold text-gray-900 hover:text-brand-red transition-colors whitespace-nowrap block"
                  >
                    7060 4788
                  </a>
                  <span className="text-xs text-gray-500">Hovednummer</span>
                </div>
                <div className="pt-1">
                  <a
                    href="tel:27524788"
                    className="text-sm font-semibold text-gray-700 hover:text-brand-red transition-colors whitespace-nowrap block"
                  >
                    2752 4788
                  </a>
                  <span className="text-[11px] text-gray-400">Direkte</span>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-red via-[#34d399] to-brand-green-light opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green-light/30 text-brand-red mb-4">
                <i className="fa-solid fa-envelope text-lg" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">E-mail</h3>
              <div className="mt-2">
                <a
                  href="mailto:kontor@cre8it.nu"
                  className="text-base font-bold text-gray-900 hover:text-brand-red transition-colors whitespace-nowrap block break-keep"
                >
                  kontor@cre8it.nu
                </a>
              </div>
            </div>

            {/* Adresse */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-red via-[#34d399] to-brand-green-light opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green-light/30 text-brand-red mb-4">
                <i className="fa-solid fa-location-dot text-lg" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Værksted & Adresse</h3>
              <div className="mt-2 text-sm text-gray-800 leading-snug">
                <p className="font-bold whitespace-nowrap">Nordre Industrivej 2a</p>
                <p className="whitespace-nowrap">6630 Rødding</p>
                <a
                  href="https://www.google.com/maps/dir//''/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x464b45024e9491d5:0x36f4b91c482f1678!3e0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-bold text-brand-red hover:underline mt-2 cursor-pointer"
                >
                  <span>Vis på kort</span> &rarr;
                </a>
              </div>
            </div>

            {/* CVR & Åbningstider */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-red via-[#34d399] to-brand-green-light opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green-light/30 text-brand-red mb-4">
                <i className="fa-solid fa-building text-lg" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Virksomhed</h3>
              <div className="mt-2 text-sm text-gray-800 space-y-1">
                <p className="font-bold whitespace-nowrap">CVR: 44682656</p>
                <p className="text-xs text-gray-500">CRE8IT</p>
                <a
                  href="https://www.facebook.com/people/Cre8it/61557070354740/?locale=da_DK"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1877F2] hover:underline pt-1.5"
                >
                  <i className="fa-brands fa-facebook text-sm" />
                  <span>Følg os på Facebook</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[9px] opacity-75" />
                </a>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 2. Formular og Kort Sektion */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-stretch">
          
          {/* Venstre side: Formular med Filupload */}
          <div className="lg:col-span-7 flex flex-col">
            <ContactForm className="h-full" />
          </div>

          {/* Højre side: Interaktivt Google Maps kort & Værkstedsinfo */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-6">
            <div className="rounded-3xl border border-gray-200/90 bg-white p-6 sm:p-8 shadow-xs space-y-4 flex flex-col flex-1">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <i className="fa-solid fa-map-location-dot text-brand-red" />
                  Find vej til værkstedet
                </h3>
                <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                  Vores professionelle værksted er indrettet med moderne faciliteter til montering af interiørfolie, bildekoration og solfilm i støvfri og opvarmede omgivelser.
                </p>
              </div>
              <div className="w-full flex-1 min-h-[220px] overflow-hidden rounded-2xl border border-gray-200 shadow-inner bg-gray-100 relative">
                <iframe
                  title="CRE8IT Rødding"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2265.0433230674514!2d9.052796977284483!3d55.37581277303117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464b45024e9491d5%3A0x36f4b91c482f1678!2sNordre%20Industrivej%202a%2C%206630%20R%C3%B8dding!5e0!3m2!1sda!2sdk!4v1720435800000!5m2!1sda!2sdk"
                  className="absolute inset-0 w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="pt-2 mt-auto">
                <a
                  href="https://www.google.com/maps/dir//''/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x464b45024e9491d5:0x36f4b91c482f1678!3e0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-gray-50 py-3 text-xs font-bold text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <i className="fa-solid fa-route text-brand-red" />
                  Åbn rutevejledning i Google Maps
                </a>
              </div>
            </div>

            {/* Smagfuldt Facebook kort */}
            <div className="rounded-3xl border border-gray-200/90 bg-white p-6 sm:p-7 shadow-xs hover:shadow-md transition-all relative overflow-hidden group shrink-0">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#1877F2]/10 text-[#1877F2] transition-transform group-hover:scale-105">
                  <i className="fa-brands fa-facebook text-2xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-gray-900 text-base">Følg os på Facebook</h4>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Aktiv
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                    Følg med i hverdagen på værkstedet, se før- og efterbilleder af nylige opgaver og hold dig opdateret med de nyeste projekter.
                  </p>
                  <div className="mt-4">
                    <a
                      href="https://www.facebook.com/people/Cre8it/61557070354740/?locale=da_DK"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl bg-[#1877F2] px-4 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-[#166fe5] hover:shadow-sm transition-all"
                    >
                      <i className="fa-brands fa-facebook-f text-xs" />
                      <span>Besøg Cre8it på Facebook</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-80" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 3. Hvem står bag Sektion */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-gray-200/90 bg-white p-8 sm:p-12 shadow-xs space-y-8">
          <div className="max-w-3xl space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Historien bag virksomheden</span>
            <h2 className="text-3xl font-bold text-gray-900">Hvem står bag CRE8IT?</h2>
          </div>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            {/* Tekst */}
            <div className="lg:col-span-8">
              {page?.content ? (
                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: page.content }} />
              ) : (
                <div className="prose max-w-none">
                  <p>
                    Mit navn er Eveline v. Sonsbeek, jeg har været i skiltefaget siden jeg var 17 år.
                  </p>
                  <p>
                    For mig er skiltefaget min passion, og jeg går meget op i at jeg kan være med fra start til slut på alle mine opgaver. Derfor har jeg i 2024 skabt min egen virksomhed.
                  </p>
                  <p>
                    Jeg har gennem årene arbejdet med alle former for grafiske løsninger, fx design, tryksager og beklædning med folie.
                  </p>
                  <p>
                    Min store passion ligger i interiørfolie, et marked som jeg har været med i helt fra starten, da den trend kom til Danmark. Med interiørfolie kan vi give dine køkkenlåger, skabslåger, kontormøbler, skriveborde et nyt og unikt look.
                  </p>
                  <p>
                    Jeg går meget op i bæredygtighed og med interiørfolie kan ældre køkkener, møbler etc. få et helt nyt liv.
                  </p>
                </div>
              )}
            </div>

            {/* Billede / Præsentation */}
            <div className="lg:col-span-4 flex justify-center">
              <div className="rounded-3xl border border-gray-200 bg-slate-50 overflow-hidden shadow-xs max-w-[320px] w-full aspect-square">
                {(() => {
                  const presentationImg =
                    (page?.mellem_billede && (Array.isArray(page.mellem_billede) ? page.mellem_billede[0] : page.mellem_billede)) ||
                    (page?.om_billede && (Array.isArray(page.om_billede) ? page.om_billede[0] : page.om_billede)) ||
                    (page?.images && page.images[0]) ||
                    (page?.billeder && page.billeder[0]) ||
                    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-1591.jpeg';
                  const presentationAlt =
                    page?.mellem_billede_alt ||
                    page?.om_billede_alt ||
                    'Eveline v. Sonsbeek - CRE8IT';

                  return (
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${presentationImg.replace('https://assets.auxodev.dk/', '')}`}
                      alt={presentationAlt}
                      className="h-full w-full object-cover"
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
